import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Col, Container, Form, Input, Modal, Row } from "reactstrap";
import { formatToEst } from "src/common/helper";
import { Loader } from "src/components";
import { useUserContext } from "src/globals";
import { Feedback, getTicketInitialValue, Ticket } from "src/sdk/models/Ticket";
import { TicketService } from "src/services/TicketService";
import { FeedbackSuperAdminColumns } from "./FeedbackSuperAdminColumns";

export interface TicketingSuperAdminDetailsProps {
    id: number,
    modal_xlarge: boolean;
    setmodal_xlarge: (value: boolean) => void;
    tog_xlarge: () => void;
}

const service = new TicketService();
export const TicketingSuperAdminDetails: React.FC<TicketingSuperAdminDetailsProps> = ({
    id,
    modal_xlarge,
    setmodal_xlarge,
    tog_xlarge,
}) => {

    const { user } = useUserContext();
    const [newFeedback, setNewFeedback] = useState("");
    const [ticket, setTicket] = useState<Ticket>(getTicketInitialValue());
    const [loading, setLoading] = useState<boolean>(true);
    const [newId, setNewId] = useState<number>(id);
    const [activeTab, setActiveTab] = useState<"description" | "feedback">("description");

    useEffect(() => {
        setNewId(id);
    }, [id]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: ticket,
        onSubmit: () => { },
    });

    useEffect(() => {
        if (!newId) return;
        setLoading(true);
        service.getTicketById(newId)
            .then((res: any) => {
                setTicket(res.data.item);
            })
            .catch((err: any) => {
                console.error(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [newId]);

    const handleSaveFeedback = () => {
        const objNewFeedback: Feedback = {
            ticketId: ticket.id,
            createdBy: user?.username,
            feedback: newFeedback
        };

        service.createFeedback(objNewFeedback)
            .then(() => {
                toast.success("Feedback created successfully");
                setNewFeedback("");

                service.getTicketById(newId)
                    .then((res: any) => {
                        setTicket(res.data.item);
                    })
                    .catch((err: any) => {
                        console.error(err);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            })
            .catch((err: any) => {
                console.error(err);
                toast.error("Error while creating feedback");
            });
    };

    return (
        <React.Fragment>
            <div className="container-fluid">
                <Modal
                    size="xl"
                    isOpen={modal_xlarge}
                    toggle={() => {
                        tog_xlarge();
                    }}
                    scrollable={true}
                >
                    <div className="modal-header d-flex align-items-center justify-content-between">
                        <h5 className="modal-title mt-0">Ticket Details - {ticket.ticketId} </h5>
                        <div className="d-flex gap-2">
                            <Button
                                color="secondary"
                                disabled={!ticket.previousId}
                                onClick={() => {
                                    if (ticket.previousId) setNewId(ticket.previousId as number);
                                    setNewFeedback("");
                                }}
                            >
                                Previous
                            </Button>
                            <Button
                                color="secondary"
                                disabled={!ticket.nextId}
                                onClick={() => {
                                    if (ticket.nextId) setNewId(ticket.nextId as number);
                                    setNewFeedback("");
                                }}
                            >
                                Next
                            </Button>
                        </div>
                        <button
                            onClick={() => {
                                setmodal_xlarge(false);
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#808080";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#AAAAAA';
                            }}
                            data-dismiss="modal"
                            style={{ backgroundColor: "#AAAAAA", color: "#FFFFFF", border: 0, borderRadius: 60, gap: 5, width: 100, paddingTop: 5, paddingBottom: 5, paddingLeft: 20, paddingRight: 20, display: "flex" }}
                            type="button"
                        >
                            <i className={`bi-x-circle`} />
                            Close
                        </button>
                    </div>
                    <div className="modal-body">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                                <Loader loading={true} />
                            </div>
                        ) : (
                            <Form
                                onSubmit={(e: any) => {
                                    e.preventDefault();
                                    validation.handleSubmit();
                                    return false;
                                }}
                            >
                                <Container fluid className="p-3">
                                    <Row className="mb-3">
                                        {ticket.facilityName && (
                                            <Col md={6}>
                                                <label className="form-label"><i className="bi bi-building text-success me-1"></i> Facility</label>
                                                <Input type="text" value={ticket.facilityName ?? "-"} readOnly disabled />
                                            </Col>
                                        )}
                                        {ticket.facilityGroupName && (
                                            <Col md={6}>
                                                <label className="form-label"><i className="bi bi-building text-success me-1"></i> Facility Group</label>
                                                <Input type="text" value={ticket.facilityGroupName ?? "-"} readOnly disabled />
                                            </Col>
                                        )}
                                        <Col md={6}>
                                            <label className="form-label">Status</label>
                                            <Input type="text" value={ticket.status ?? "-"} readOnly disabled />
                                        </Col>
                                    </Row>
                                    <Row className="mb-4">
                                        <Col md={12}>
                                            <label className="form-label">
                                                <i className="bi bi-telephone text-success me-1"></i>
                                                Contact
                                            </label>
                                            <Input type="text" value={ticket.contact ?? "-"} readOnly disabled />
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <label className="form-label">Ticket Created By</label>
                                            <Input type="text" value={ticket.createdBy ?? "-"} readOnly disabled />
                                        </Col>
                                        <Col md={6}>
                                            <label className="form-label">Ticket Created At</label>
                                            <Input
                                                type="text"
                                                value={formatToEst(ticket.createdAt)}
                                                readOnly
                                                disabled
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mb-5">
                                        <Col md={12}>
                                            <label className="form-label">Subject</label>
                                            <Input type="textarea" value={ticket.subject ?? "-"} readOnly disabled rows={6} />
                                        </Col>
                                    </Row>
                                    <hr style={{ marginTop: '80px', marginBottom: '16px' }} />


                                    <div className="d-flex gap-4 mb-1 pb-2">
                                        <label
                                            className={`form-label mb-0  cursor-pointer ${activeTab === "description" ? "text-success" : "fw-bold"}`}
                                            onClick={() => setActiveTab("description")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            Description
                                        </label>
                                        <label
                                            className={`form-label mb-0 cursor-pointer ${activeTab === "feedback" ? "text-success" : "fw-bold"}`}
                                            onClick={() => setActiveTab("feedback")}
                                            style={{ cursor: "pointer" }}
                                        >
                                            Feedback ({ticket.feedbackItems?.length || 0})
                                        </label>
                                    </div>
                                    {activeTab === "description" && (
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <Input type="textarea" value={ticket.description ?? ""} readOnly disabled rows={6} />
                                            </Col>
                                        </Row>
                                    )}

                                    {activeTab === "feedback" && (
                                        <>
                                            <DataTable
                                                columns={FeedbackSuperAdminColumns()}
                                                data={ticket.feedbackItems || []}
                                                noDataComponent="No feedback available."
                                                striped
                                                highlightOnHover
                                                responsive
                                                dense
                                            />
                                            <Row className="mb-3 mt-5">
                                                <Col md={12}>
                                                    <Input
                                                        type="textarea"
                                                        rows={6}
                                                        value={newFeedback}
                                                        onChange={(e) => setNewFeedback(e.target.value)}
                                                        placeholder="Add your feedback here..."
                                                    />
                                                </Col>
                                            </Row>
                                            <div className="d-flex flex-row-reverse">
                                                <div className="p-1">
                                                    <Button
                                                        color="primary"
                                                        style={{ backgroundColor: "#95CBB2", border: 0, color: "#FFFFFF" }}
                                                        onClick={handleSaveFeedback}
                                                    >
                                                        Submit Feedback
                                                    </Button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </Container>
                            </Form>
                        )}
                    </div>
                </Modal>
            </div>
        </React.Fragment>
    );
}

TicketingSuperAdminDetails.propTypes = {

};
export default withTranslation()(TicketingSuperAdminDetails);
